import React, { useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'src/utils/dayjs';
import Button from 'src/components/Button';
import { DOWNLOAD_CFDI_RECEIVED_IN_ZIP } from 'src/store/constants/CFDI';
import Loading from 'src/components/Loading';
import { downloadCFDIsReceivedListInZip } from 'src/store/actions/cfdiActions';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import DialogTransition from 'src/components/DialogTransition';
import validationSchema from './schema';
import useStyles from './styles';

const makeInitialState = () => ({
  month: dayjs.tz()
});

const DownloadCFDIReceivedListDialog = ({ open, onClose }) => {
  const { classes } = useStyles();
  const resetRef = useRef();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const initialState = useMemo(() => makeInitialState(), []);
  const loadingCFDIReceivedDownload = useSelector(
    isLoadingSelector([DOWNLOAD_CFDI_RECEIVED_IN_ZIP.action])
  );

  const handleDownloadInvoice = values => {
    dispatch(
      downloadCFDIsReceivedListInZip(
        {
          ...values,
          month: values.month ? dayjs(values.month).toDate() : values.month
        },
        onClose
      )
    );
  };

  useEffect(() => {
    return () => {
      if (resetRef.current) {
        resetRef.current();
      }
    };
  }, [resetRef, open]);

  if (!open) return null;

  return (
    <div>
      <Dialog
        classes={{ paper: classes.root }}
        open={open}
        scroll="body"
        TransitionComponent={DialogTransition}
        keepMounted
        disableEscapeKeyDown
      >
        <DialogTitle className={classes.title}>
          <IconButton
            key="close"
            color="inherit"
            onClick={onClose}
            className={classes.closeIcon}
          >
            <CloseIcon />
          </IconButton>
          <Typography className={classes.title} color="primary" variant="h2">
            {t('COMMON.DOWNLOAD_CFDIS')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {loadingCFDIReceivedDownload ? (
            <Loading />
          ) : (
            <Formik
              initialValues={initialState}
              validationSchema={validationSchema}
              onSubmit={handleDownloadInvoice}
              enableReinitialize
            >
              {({
                errors,
                handleBlur,
                setFieldValue,
                handleSubmit,
                touched,
                values,
                resetForm
              }) => {
                resetRef.current = () => resetForm(initialState);
                return (
                  <form onSubmit={handleSubmit}>
                    <DatePicker
                      format="MM/yyyy"
                      views={['month']}
                      disableFuture
                      handleBlur={handleBlur}
                      label={t('COMMON.MONTH')}
                      value={values.month}
                      onChange={newValue => {
                        setFieldValue(
                          'month',
                          newValue ? dayjs(newValue) : ''
                        );
                      }}
                      slotProps={{
                        textField: {
                          sx: { width: '100%', margin: '16px 0' },
                          className: classes.datepicker,
                          error: Boolean(touched.month && errors.month),
                          helperText: touched.month && t(errors.month),
                          InputProps: { autoComplete: 'off' }
                        }
                      }}
                    />
                    <Grid container direction="column" alignItems="stretch">
                      <Grid item>
                        <Button type="submit">
                          {t('COMMON.DOWNLOAD_CFDIS')}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                );
              }}
            </Formik>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

DownloadCFDIReceivedListDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};
export default DownloadCFDIReceivedListDialog;
